import {config} from "../../config";
import {ColorBet} from "../betTypes/ColorBet";
import {ColumnBet} from "../betTypes/ColumnBet";
import {CornerBet} from "../betTypes/CornerBet";
import {DozenBet} from "../betTypes/DozenBet";
import {EvenOddBet} from "../betTypes/EvenOddBet";
import {IRouletteBet} from "../betTypes/IRouletteBet";
import {LineBet} from "../betTypes/LineBet";
import {LowHighNumbersBet} from "../betTypes/LowHighNumbersBet";
import {SplitBet} from "../betTypes/SplitBet";
import {StraightBet} from "../betTypes/StraightBet";
import {StreetBet} from "../betTypes/StreetBet";
import {CornerBetsPanel} from "../userInterface/rouletteBetsPanel/implementations/CornerBetsPanel";
import {SplitBetsPanel} from "../userInterface/rouletteBetsPanel/implementations/SplitBetsPanel";
import {StreetBetsPanel} from "../userInterface/rouletteBetsPanel/implementations/StreetBetsPanel";
import {Utils} from "../Utils";
import {RouletteBets} from "./RouletteBetsProtocol";

export class RouletteBetsBuilder {

    private totalBet = 0;
    private rouletteBetsProtocol: RouletteBets = new RouletteBets();

    public addColumnBet(selectedIndex: number, betAmount: number): void {
        this.addBet<ColumnBet>(ColumnBet, "columnBets", "index", config.columnBetMax, selectedIndex, betAmount);
    }

    public addStraightBet(selectedNumber: number, betAmount: number): void {
        this.throwErrorIfBetOnOneNumberToHigh(selectedNumber, betAmount);
        this.addBet<StraightBet>(StraightBet, "straightBets", "chosenNumber", config.straightBetMax, selectedNumber, betAmount);
    }

    public addSplitBet(selectedIndex: number, betAmount: number): void {
        for (let i = 0; i < SplitBetsPanel.POSSIBLE_SPLIT_BETS[selectedIndex].length; i++) {
            this.throwErrorIfBetOnOneNumberToHigh(SplitBetsPanel.POSSIBLE_SPLIT_BETS[selectedIndex][i], betAmount);
        }
        this.addBet<SplitBet>(SplitBet, "splitBets", "index", config.splitBetMax, selectedIndex, betAmount);
    }

    public addCornerBet(selectedIndex: number, betAmount: number): void {
        for (let i = 0; i < CornerBetsPanel.POSSIBLE_CORNER_BETS[selectedIndex].length; i++) {
            this.throwErrorIfBetOnOneNumberToHigh(CornerBetsPanel.POSSIBLE_CORNER_BETS[selectedIndex][i], betAmount);
        }
        this.addBet<CornerBet>(CornerBet, "cornerBets", "index", config.cornerBetMax, selectedIndex, betAmount);
    }

    public addStreetBet(selectedIndex: number, betAmount: number): void {
        for (let i = 0; i < StreetBetsPanel.POSSIBLE_STREET_BETS[selectedIndex].length; i++) {
            this.throwErrorIfBetOnOneNumberToHigh(StreetBetsPanel.POSSIBLE_STREET_BETS[selectedIndex][i], betAmount);
        }
        this.addBet<StreetBet>(StreetBet, "streetBets", "index", config.streetBetMax, selectedIndex, betAmount);
    }

    public addLineBet(selectedIndex: number, betAmount: number): void {
        this.addBet<LineBet>(LineBet, "lineBets", "index", config.lineBetMax, selectedIndex, betAmount);
    }

    public addDozenBet(selectedIndex: number, betAmount: number): void {
        this.addBet<DozenBet>(DozenBet, "dozenBets", "index", config.dozenBetMax, selectedIndex, betAmount);
    }

    public addLowHighNumbersBet(lowNumbers: boolean, betAmount: number): void {
        this.addBet<LowHighNumbersBet>(LowHighNumbersBet, "lowHighNumbersBets", "lowNumbers", config.lowHighNumbersBetMax, lowNumbers, betAmount);
    }

    public addEvenOddBet(evenNumbers: boolean, betAmount: number): void {
        this.addBet<EvenOddBet>(EvenOddBet, "evenOddBets", "evenNumbers", config.evenOddBetMax, evenNumbers, betAmount);
    }

    public addColorBet(redColor: boolean, betAmount: number): void {
        this.addBet<ColorBet>(ColorBet, "colorBets", "redColor", config.colorBetMax, redColor, betAmount);
    }

    public decreaseColumnBet(selectedIndex: number, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.columnBets.findIndex((columnBetFromArr: ColumnBet) => {
            return columnBetFromArr.index === selectedIndex;
        });

        this.rouletteBetsProtocol.columnBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.columnBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.columnBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.columnBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseStraightBet(selectedNumber: number, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.straightBets.findIndex((straightBetFromArr: StraightBet) => {
            return straightBetFromArr.chosenNumber === selectedNumber;
        });

        this.rouletteBetsProtocol.straightBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.straightBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.straightBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.straightBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseSplitBet(selectedIndex: number, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.splitBets.findIndex((splitBetFromArr: SplitBet) => {
            return splitBetFromArr.index === selectedIndex;
        });

        this.rouletteBetsProtocol.splitBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.splitBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.splitBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.splitBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseCornerBet(selectedIndex: number, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.cornerBets.findIndex((cornerBetFromArr: CornerBet) => {
            return cornerBetFromArr.index === selectedIndex;
        });

        this.rouletteBetsProtocol.cornerBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.cornerBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.cornerBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.cornerBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseStreetBet(selectedIndex: number, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.streetBets.findIndex((streetBetFromArr: StreetBet) => {
            return streetBetFromArr.index === selectedIndex;
        });

        this.rouletteBetsProtocol.streetBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.streetBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.streetBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.streetBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseLineBet(selectedIndex: number, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.lineBets.findIndex((lineBetFromArr: LineBet) => {
            return lineBetFromArr.index === selectedIndex;
        });

        this.rouletteBetsProtocol.lineBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.lineBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.lineBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.lineBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseDozenBet(selectedIndex: number, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.dozenBets.findIndex((dozenBetFromArr: DozenBet) => {
            return dozenBetFromArr.index === selectedIndex;
        });

        this.rouletteBetsProtocol.dozenBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.dozenBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.dozenBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.dozenBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseLowHighNumbersBet(lowNumbers: boolean, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.lowHighNumbersBets.findIndex(
            (lowHighNumbersBetFromArr: LowHighNumbersBet) => {
                return lowHighNumbersBetFromArr.lowNumbers === lowNumbers;
            });

        this.rouletteBetsProtocol.lowHighNumbersBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.lowHighNumbersBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.lowHighNumbersBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.lowHighNumbersBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseEvenOddBet(evenNumbers: boolean, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.evenOddBets.findIndex(
            (lowHighNumbersBetFromArr: EvenOddBet) => {
                return lowHighNumbersBetFromArr.evenNumbers === evenNumbers;
            });

        this.rouletteBetsProtocol.evenOddBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.evenOddBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.evenOddBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.evenOddBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public decreaseColorBet(redColor: boolean, amount: number): void {
        const betIndex = this.rouletteBetsProtocol.colorBets.findIndex(
            (colorBetFromArr: ColorBet) => {
                return colorBetFromArr.redColor === redColor;
            });

        this.rouletteBetsProtocol.colorBets[betIndex].betAmount =
            Utils.roundWithPrecision(this.rouletteBetsProtocol.colorBets[betIndex].betAmount - amount, 3);
        if (this.rouletteBetsProtocol.colorBets[betIndex].betAmount === 0) {
            this.rouletteBetsProtocol.colorBets.splice(betIndex, 1);
        }
        this.totalBet -= amount;
        this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
    }

    public getStraightBet(selectedNumber: number): StraightBet {
        const betIndex = this.rouletteBetsProtocol.straightBets.findIndex((straightBetFromArr: StraightBet) => {
            return straightBetFromArr.chosenNumber === selectedNumber;
        });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.straightBets[betIndex];
        } else {
            return new StraightBet(selectedNumber, 0);
        }
    }

    public getSplitBet(selectedIndex: number): SplitBet {
        const betIndex = this.rouletteBetsProtocol.splitBets.findIndex((splitBetFromArr: SplitBet) => {
            return splitBetFromArr.index === selectedIndex;
        });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.splitBets[betIndex];
        } else {
            return new SplitBet(selectedIndex, 0);
        }
    }

    public getCornerBet(selectedIndex: number): CornerBet {
        const betIndex = this.rouletteBetsProtocol.cornerBets.findIndex((cornerBetFromArr: CornerBet) => {
            return cornerBetFromArr.index === selectedIndex;
        });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.cornerBets[betIndex];
        } else {
            return new CornerBet(selectedIndex, 0);
        }
    }

    public getLineBet(selectedIndex: number): LineBet {
        const betIndex = this.rouletteBetsProtocol.lineBets.findIndex((lineBetFromArr: LineBet) => {
            return lineBetFromArr.index === selectedIndex;
        });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.lineBets[betIndex];
        } else {
            return new LineBet(selectedIndex, 0);
        }
    }

    public getStreetBet(selectedIndex: number): LineBet {
        const betIndex = this.rouletteBetsProtocol.streetBets.findIndex((streetBetFromArr: StreetBet) => {
            return streetBetFromArr.index === selectedIndex;
        });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.streetBets[betIndex];
        } else {
            return new StreetBet(selectedIndex, 0);
        }
    }

    public getColumnBet(selectedIndex: number): ColumnBet {
        const betIndex = this.rouletteBetsProtocol.columnBets.findIndex((columnBetFromArr: ColumnBet) => {
            return columnBetFromArr.index === selectedIndex;
        });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.columnBets[betIndex];
        } else {
            return new ColumnBet(selectedIndex, 0);
        }
    }

    public getDozenBet(selectedIndex: number): DozenBet {
        const betIndex = this.rouletteBetsProtocol.dozenBets.findIndex((dozenBetFromArr: DozenBet) => {
            return dozenBetFromArr.index === selectedIndex;
        });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.dozenBets[betIndex];
        } else {
            return new DozenBet(selectedIndex, 0);
        }
    }

    public getLowHighNumbersBet(lowNumbers: boolean): LowHighNumbersBet {
        const betIndex = this.rouletteBetsProtocol.lowHighNumbersBets.findIndex(
            (lowHighNumberBetFromArr: LowHighNumbersBet) => {
                return lowHighNumberBetFromArr.lowNumbers === lowNumbers;
            });

        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.lowHighNumbersBets[betIndex];
        } else {
            return new LowHighNumbersBet(lowNumbers, 0);
        }
    }

    public getEvenOddBet(evenNumbers: boolean): EvenOddBet {
        const betIndex = this.rouletteBetsProtocol.evenOddBets.findIndex(
            (evenOddBetFromArr: EvenOddBet) => {
                return evenOddBetFromArr.evenNumbers === evenNumbers;
            });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.evenOddBets[betIndex];
        } else {
            return new EvenOddBet(evenNumbers, 0);
        }
    }

    public getColorBet(redColor: boolean): ColorBet {
        const betIndex = this.rouletteBetsProtocol.colorBets.findIndex(
            (colorBetFromArr: ColorBet) => {
                return colorBetFromArr.redColor === redColor;
            });
        if (betIndex !== -1) {
            return this.rouletteBetsProtocol.colorBets[betIndex];
        } else {
            return new ColorBet(redColor, 0);
        }
    }

    public getTotalBetAmount(): number {
        return this.totalBet;
    }

    public build(): RouletteBets {
        return this.rouletteBetsProtocol;
    }

    public clear(): void {
        this.rouletteBetsProtocol = new RouletteBets();
        this.totalBet = 0;
    }

    private addBet<T extends IRouletteBet>(BetType: new (...args: any[]) => T, betTypeProperty: string, betIdentifierName: string,
                                           maxBet: number, selectedValue: any, betAmount: number): void {
        this.throwErrorIfTotalBetExceedsMaxTotalBet(betAmount);
        const betIndex = this.rouletteBetsProtocol[betTypeProperty].findIndex((bet: T) => {
            return bet[betIdentifierName] === selectedValue;
        });

        if (betIndex !== -1 &&
            Utils.roundWithPrecision(this.rouletteBetsProtocol[betTypeProperty][betIndex].betAmount, 3) === maxBet) {
            throw new Error("Bet Area Max Bet Exceeded.");
        }

        if ((betIndex === -1 && betAmount >= maxBet)) {
            this.rouletteBetsProtocol[betTypeProperty].push(new BetType(selectedValue, maxBet));
            this.totalBet += maxBet;
        } else if (betIndex === -1) {
            this.rouletteBetsProtocol[betTypeProperty].push(new BetType(selectedValue, betAmount));
            this.totalBet += betAmount;
        } else if ((betIndex !== -1 && this.rouletteBetsProtocol[betTypeProperty][betIndex].betAmount + betAmount) >= maxBet) {
            const betValueBefore = this.rouletteBetsProtocol[betTypeProperty][betIndex].betAmount;
            this.rouletteBetsProtocol[betTypeProperty][betIndex].betAmount = Utils.roundWithPrecision(maxBet, 3);
            this.totalBet += (maxBet - betValueBefore);
        } else if (betIndex !== -1) {
            this.rouletteBetsProtocol[betTypeProperty][betIndex].betAmount =
                Utils.roundWithPrecision(betAmount + this.rouletteBetsProtocol[betTypeProperty][betIndex].betAmount, 3);
            this.totalBet += betAmount;
            this.totalBet = Utils.roundWithPrecision(this.totalBet, 3);
        }
    }

    private throwErrorIfBetOnOneNumberToHigh(selectedNumber: number, currentBetAmountPassed: number): void {
        let totalBetAmount = currentBetAmountPassed;
        let betIndex = this.rouletteBetsProtocol.straightBets.findIndex((straightBetFromArr: StraightBet) => {
            return straightBetFromArr.chosenNumber === selectedNumber;
        });
        totalBetAmount +=
            betIndex >= 0 ? this.rouletteBetsProtocol.straightBets[betIndex].betAmount : 0;

        const splitBetIndexes = this.getAllIndexes(SplitBetsPanel.POSSIBLE_SPLIT_BETS, selectedNumber);

        for (let i = 0; i < splitBetIndexes.length; i++) {
            betIndex = this.rouletteBetsProtocol.splitBets.findIndex((splitBetFromArr: SplitBet) => {
                return splitBetFromArr.index === splitBetIndexes[i];
            });
            totalBetAmount +=
                betIndex >= 0 ? this.rouletteBetsProtocol.splitBets[betIndex].betAmount : 0;
        }

        const cornerBetIndexes = this.getAllIndexes(CornerBetsPanel.POSSIBLE_CORNER_BETS, selectedNumber);

        for (let i = 0; i < cornerBetIndexes.length; i++) {
            betIndex = this.rouletteBetsProtocol.cornerBets.findIndex((cornerBetFromArr: CornerBet) => {
                return cornerBetFromArr.index === cornerBetIndexes[i];
            });
            totalBetAmount +=
                betIndex >= 0 ? this.rouletteBetsProtocol.cornerBets[betIndex].betAmount : 0;
        }

        const streetBetIndexes = this.getAllIndexes(StreetBetsPanel.POSSIBLE_STREET_BETS, selectedNumber);

        for (let i = 0; i < streetBetIndexes.length; i++) {
            betIndex = this.rouletteBetsProtocol.streetBets.findIndex((streetBetFromArr: StreetBet) => {
                return streetBetFromArr.index === streetBetIndexes[i];
            });
            totalBetAmount +=
                betIndex >= 0 ? this.rouletteBetsProtocol.streetBets[betIndex].betAmount : 0;
        }

        if (totalBetAmount > config.oneNumberMax) {
            throw new Error("One Number Max Bet Exceeded.");
        }
    }

    private throwErrorIfTotalBetExceedsMaxTotalBet(betAmount: number): void {
        if (this.totalBet + betAmount > config.totalBetMax) {
            throw new Error("Max total bet exceeded.");
        }
    }

    private getAllIndexes(arr: Array<Array<any>>, val: number): Array<number> {
        const indexes = [];
        for (let i = 0; i < arr.length; i++) {
            for (let j = 0; j < arr.length; j++) {
                if (arr[i][j] === val) {
                    indexes.push(i);
                }
            }
        }
        return indexes;
    }
}



// WEBPACK FOOTER //
// ./src/app/api/RouletteBetsBuilder.ts